import { Box, useTheme } from "@mui/material";
import { TokenIcon } from "components/TokenIcon";
import { useThemeStore } from "hooks/useTheme";
import { Token } from "types/tokens";
import { Button } from "components/Button";
import { DangerButton } from "components/Button/Danger";
import { MdLockOpen, MdLock, MdOutlineSync } from "react-icons/md";
import { tokensService } from "services/tokensService";
import { useState } from "react";
import clsx from "clsx";

type Props = {
  token: Token;
  isAdmin: boolean;
	onSuccess: () => void;
};

export const TokenElement: React.FC<Props> = ({ token, isAdmin, onSuccess }) => {
  const theme = useTheme();
  const { mode } = useThemeStore();
  const { isBlocked, id } = token;
  const [isLoading, setIsloading] = useState(false);

  const toggleTokenIsBlocked = async () => {
    try {
      setIsloading(true);
      await tokensService.putToken({ id, isBlocked: !isBlocked });
      onSuccess()
      setIsloading(false);
    } catch (error) {
      console.error(error);
      setIsloading(false);
    }
  };

  return (
    <Box
      component={"li"}
      sx={{
        width: "100%",
        padding: 0,
        cursor: "pointer",
        color: `${theme.palette.text.primary} !important`,
        textTransform: "capitalize",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          minHeight: "48px",
          alignItems: "center",
          backgroundColor: "#f4f7fe",
          borderRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
          flex: "1",
          transition: "all 300ms ease",
          px: { xs: "8px !important", sm: "18px !important" },
          "@media (max-width: 600px)": {
            minHeight: "56px",
            borderRadius: "8px",
            gap: "14px",
          },
          "&:hover": {
            backgroundColor: mode === "light" ? "rgba(0,0,0,.075)" : "#1e1b43",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "10px", sm: "16px" },
          }}
        >
          <Box
            sx={{
              maxWidth: { xs: "22px", sm: "26px" },
            }}
          >
            <TokenIcon src={token?.iconUrl} address={token?.address}>
              {(src: string) => (
                <img style={{ width: "100%" }} src={src} alt="coinIcon" />
              )}
            </TokenIcon>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", sm: "12px" },
            }}
          >
            <Box
              component={"span"}
              sx={{
                textTransform: { xs: "uppercase", sm: "initial" },
                fontSize: { xs: "12px", sm: "14px" },
                fontWeight: 500,
              }}
            >
              {token?.name}
            </Box>
            <Box
              component={"span"}
              sx={{
                textTransform: "uppercase",
                color: "#5D6880",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {token?.symbol}
            </Box>
          </Box>
        </Box>

        <Box style={{ display: "flex", gap: "24px" }}>
          <span
            className={clsx(
              "rounded-md p-1 text-white",
              isBlocked ? "bg-red-500" : "bg-green-500"
            )}
          >
            {isBlocked ? "Blocked" : "Unblocked"}
          </span>
          {isBlocked ? (
            <DangerButton
              disabled={isLoading || !isAdmin}
              onClick={toggleTokenIsBlocked}
            >
              {isLoading ? <MdOutlineSync /> : <MdLockOpen />}
            </DangerButton>
          ) : (
            <Button
              disabled={isLoading || !isAdmin}
              onClick={toggleTokenIsBlocked}
            >
              {isLoading ? <MdOutlineSync /> : <MdLock />}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
